import React, { FC, MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { AlertColor, Box, CircularProgress, IconButton, Link, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Form, { FormDataModel } from '../../../components/Form';
import { LoginForm } from './LoginForm';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import { removeErrorFieldsFromValues, Validators, regex } from '../../../utils/validators';
import { routes } from '../../../utils/constants/routes';
import axiosInstance from '../../../utils/axios';
import RecaptchaNotice from '../../../components/Captcha/RecaptchaNotice';
import strings from '../../../common/Translation/Translate';
import InputField from '../../../components/InputField';
import { URLS } from 'utils/constants/urls';
import { RECAPTCHA_ACTIONS } from '../../../utils/recaptchaActions';

export type LoginStep = 'identifier' | 'password' | 'otp';

function isEmail(value: string): boolean {
  return regex.email.test((value || '').trim());
}

function isMobile(value: string): boolean {
  return regex.mobile.test((value || '').trim().replace(/\s/g, ''));
}

function validateIdentifier(value: string): { error: boolean; message: string } | null {
  const v = (value || '').trim();
  if (!v) return { error: true, message: strings.requiredText };
  if (isEmail(v)) return null;
  if (isMobile(v)) return null;
  return { error: true, message: strings.login_enter_email_or_mobile || 'Please enter a valid email or mobile number' };
}

interface MobileEmailLoginProps {
  language: string;
  languageData: any[];
  theme: any;
  setMessage: (message: { display: boolean; severity: AlertColor | null; message: string }) => void;
  Navigate: any;
  dispatch: any;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  handleLoginSuccess: (data: any, identifier: string) => void;
}

const MobileEmailLogin: FC<MobileEmailLoginProps> = ({
  theme,
  setMessage,
  Navigate,
  loading,
  setLoading,
  handleLoginSuccess,
}) => {
  let loginForm: RefObject<Form | null | undefined> = useRef();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [step, setStep] = useState<LoginStep>('identifier');
  const [identifier, setIdentifier] = useState<string>('');
  const [identifierTouched, setIdentifierTouched] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendCountdown, setResendCountdown] = useState<number>(0);
  const [otpCode, setOtpCode] = useState<string>('');

  const goToIdentifier = useCallback(() => {
    setStep('identifier');
    setIdentifierTouched(false);
    setOtpCode('');
    setResendCountdown(0);
  }, []);

  const handleIdentifierNext = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIdentifierTouched(true);
    const err = validateIdentifier(identifier);
    if (err) return;
    const trimmed = identifier.trim();
    if (isEmail(trimmed)) {
      setStep('password');
      return;
    }
    if (isMobile(trimmed)) {
      try {
        if (!executeRecaptcha) {
          setMessage({
            display: true,
            severity: 'warning',
            message: 'reCAPTCHA is not ready',
          });
          return;
        }
        setLoading(true);
        const captchaRes = await executeRecaptcha(RECAPTCHA_ACTIONS.login);
        await axiosInstance.post(URLS.forgot, {
          identifier: trimmed,
          mailType: 'MOBILE_OTP',
          captchaResponse: {
            captchaRes,
            captchaAction: RECAPTCHA_ACTIONS.login,
          },
        });
        setMessage({
          display: true,
          message: strings.code_validated_success_message || 'Code sent successfully.',
          severity: 'success',
        });
        setResendCountdown(60);
        setStep('otp');
      } catch (error: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: error?.response?.data?.message || error?.message || strings.tryAgainMessage,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResendOtp = async () => {
    if (resendCountdown > 0 || resendLoading) return;
    try {
      setResendLoading(true);
      await axiosInstance.post(URLS.resend, {
        identifier: identifier.trim(),
        mailType: 'MOBILE_OTP',
      });
      setMessage({
        display: true,
        message: strings.code_validated_success_message || 'Code sent successfully.',
        severity: 'success',
      });
      setResendCountdown(60);
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: error?.response?.data?.message || error?.message || strings.tryAgainMessage,
      });
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleLogin = useCallback(
    async (e: MouseEvent<HTMLButtonElement>, loginType?: string) => {
      e.preventDefault();

      if (!executeRecaptcha) {
        setMessage({
          display: true,
          severity: 'warning',
          message: 'reCAPTCHA is not ready',
        });
        return;
      }

      if (loginType === 'MOBILE_OTP') {
        const code = otpCode.trim();
        if (!code || code.length < 6) {
          setMessage({
            display: true,
            severity: 'warning',
            message: strings.requiredText || 'Please enter the 6-digit code',
          });
          return;
        }
        try {
          setOtpLoading(true);
          await axiosInstance.post(URLS.verifyCode, {
            identifier: identifier.trim(),
            mailType: 'MOBILE_OTP',
            code,
          });
          const captchaRes = await executeRecaptcha(RECAPTCHA_ACTIONS.login);
          const { status, data } = await axiosInstance.post(URLS.login, {
            loginType: 'MOBILE_OTP',
            mobile: identifier.trim(),
            otp: code,
            captchaResponse: {
              captchaRes,
              captchaAction: RECAPTCHA_ACTIONS.login,
            },
          });
          if (status === 200) {
            handleLoginSuccess(data, identifier);
          }
        } catch (error: any) {
          if (error?.response?.status === 403) {
            Navigate(`${routes.setPasswordAfterUpgrade}?email=${encodeURIComponent(identifier)}`, {
              replace: true,
            });
            return;
          }
          setMessage({
            display: true,
            severity: 'error',
            message:
              error?.response?.data?.error_description ||
              error?.response?.data?.message ||
              error?.message ||
              strings.tryAgainMessage,
          });
        } finally {
          setOtpLoading(false);
          setLoading(false);
        }
      } else {
        const { getFormData } = loginForm.current as {
          getFormData: () => { formData: FormDataModel; isFormValid: boolean };
        };
        const { formData, isFormValid } = getFormData();
        const bodyData = removeErrorFieldsFromValues(formData);

        try {
          setHasError(false);
          if (isFormValid) {
            setLoading(true);
            const captchaRes = await executeRecaptcha(RECAPTCHA_ACTIONS.login);
            const { status, data } = await axiosInstance.post(URLS.login, {
              loginType: 'EMAIL_PASSWORD',
              username: identifier.trim(),
              password: bodyData.password,
              captchaResponse: {
                captchaRes,
                captchaAction: RECAPTCHA_ACTIONS.login,
              },
            });
            if (status === 200) {
              handleLoginSuccess(data, identifier);
            }
          } else {
            setHasError(true);
          }
        } catch (error: any) {
          if (error?.response?.status === 403) {
            Navigate(`${routes.setPasswordAfterUpgrade}?email=${encodeURIComponent(identifier)}`, {
              replace: true,
            });
            return;
          }
          if (error) {
            const errorMessage = error.response ? error.response.data.error_description : error.message;
            setMessage({
              display: true,
              severity: 'warning',
              message: errorMessage || error.response.data.message,
            });
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [otpCode, executeRecaptcha, setMessage, identifier, handleLoginSuccess, Navigate, setLoading],
  );

  const renderIdentifierStep = () => (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleIdentifierNext(e as any);
      }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
        {strings.login_enter_email_or_mobile}
      </Typography>
      <InputField
        data-testid="login-identifier-input"
        autoFocus
        label={strings.login_identifier_placeholder}
        inputValue={identifier}
        textChange={value => setIdentifier(value)}
        validators={[
          {
            check: (v: string) => (identifierTouched ? validateIdentifier(v) || false : false),
            message: strings.login_enter_email_or_mobile || 'Please enter a valid email or mobile number',
          },
        ]}
        hasError={identifierTouched && !!validateIdentifier(identifier)}
        fieldError={identifierTouched && !!validateIdentifier(identifier)}
        placeholder={strings.login_identifier_placeholder}
        variant="outlined"
        size="medium"
        sx={{ mb: 2 }}
      />
      <PrimaryButton
        data-testid="login-next-btn"
        disabled={loading}
        sx={{ mt: 3, py: 1.25, fontSize: '1rem', fontWeight: 600, textTransform: 'none', borderRadius: 1 }}
        onClick={handleIdentifierNext}
        type="submit"
        fullWidth>
        {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
        {strings.login_next}
      </PrimaryButton>
      <RecaptchaNotice />
    </form>
  );

  const renderPasswordStep = useCallback(
    () => (
      <form onSubmit={handleLogin as any}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {isEmail(identifier) ? 'Email:' : 'Mobile:'} <strong>{identifier.trim()}</strong>
          </Typography>
          <IconButton size="small" onClick={goToIdentifier} sx={{ color: theme.palette.primary.main }}>
            <Edit fontSize="small" />
          </IconButton>
        </Box>
        <Form
          hasError={hasError}
          ref={loginForm as RefObject<Form>}
          model={LoginForm(strings).filter(field => field.field === 'password')}
          values={{}}
        />
        <PrimaryButton
          disabled={loading}
          sx={{ mt: 3, py: 1.25, fontSize: '1rem', fontWeight: 600, textTransform: 'none', borderRadius: 1 }}
          onClick={handleLogin}
          type="submit"
          fullWidth>
          {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
          {strings.loginText}
        </PrimaryButton>
        <RecaptchaNotice />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={goToIdentifier}>
            {strings.login_back} / {strings.login_use_another_method}
          </Link>
        </Typography>
      </form>
    ),
    [handleLogin, identifier, goToIdentifier, theme.palette.primary.main, hasError, loading],
  );

  const renderOtpStep = () => (
    <>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" color="text.secondary">
          {strings.login_otp_sent_message} <strong>{identifier.trim()}</strong>
        </Typography>
        <IconButton size="small" onClick={goToIdentifier} sx={{ color: theme.palette.primary.main }}>
          <Edit fontSize="small" />
        </IconButton>
      </Box>
      <InputField
        data-testid="login-otp-input"
        autoFocus
        inputValue={otpCode}
        textChange={setOtpCode}
        label={strings.login_enter_otp}
        validators={[{ check: Validators.validateMandatoryField, message: strings.requiredText }]}
        inputProps={{ maxLength: 6 }}
        variant="outlined"
        size="medium"
        sx={{ mb: 2 }}
      />
      <PrimaryButton
        data-testid="login-verify-otp-btn"
        disabled={otpLoading || otpCode.trim().length < 6}
        sx={{ mt: 2, py: 1.25, fontSize: '1rem', fontWeight: 600, textTransform: 'none', borderRadius: 1 }}
        onClick={e => handleLogin(e, 'MOBILE_OTP')}
        fullWidth>
        {otpLoading && <CircularProgress sx={{ mr: 1 }} size={20} />}
        {strings.login_verify_and_login}
      </PrimaryButton>
      <RecaptchaNotice />
      <Box mt={2} display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={0.5}>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {strings.login_didnt_receive_code}
        </Typography>
        <Typography
          component="span"
          data-testid="login-resend-otp-btn"
          onClick={handleResendOtp}
          sx={{
            ml: 0.5,
            color: resendCountdown > 0 ? 'text.secondary' : theme.palette.primary.main,
            cursor: resendCountdown > 0 ? 'not-allowed' : 'pointer',
            textDecoration: resendCountdown > 0 ? 'none' : 'underline',
          }}>
          {resendLoading
            ? strings.login_sending
            : resendCountdown > 0
            ? `${strings.login_resend_in} ${resendCountdown}s`
            : strings.login_resend_code}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={goToIdentifier}>
          {strings.login_back} / {strings.login_use_another_method}
        </Link>
      </Typography>
    </>
  );

  return (
    <>
      {step === 'identifier' && renderIdentifierStep()}
      {step === 'password' && renderPasswordStep()}
      {step === 'otp' && renderOtpStep()}
    </>
  );
};

export default MobileEmailLogin;
